import styled from 'styled-components';
import { Switch, Text } from '@lmn/design-system';
import type { FilterChange } from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersCommon';
import { getCompaniesFilterChangeData } from '@/app/components/hotel-detail/changeTransport/ChangeTransportCompaniesFilter';
import { useTransportPageFilterTracking } from '@/app/components/hotel-detail/changeTransport/hooks/useTransportPageFilterTracking';
import { isSameAirlineFilterEnabled } from '@/app/components/hotel-detail/travel-api/transportsFetch';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import {
  EvoCheckboxRow,
  EvoFilterDivider,
  EvoFilterLoadingLayer,
  EvoFilterSection,
} from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/filters/EvoFilterShared';

const SameAirlineToggleRow = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  column-gap: 8px;
  padding: 12px 0;
`;

const SameAirlineLabel = styled.label`
  cursor: pointer;
`;

const SAME_AIRLINE_SWITCH_ID = 'evo-same-airline-switch';

type EvoAirlinesFilterProps = {
  companies?: CompanyFilter[];
  sameAirline?: SameAirportFilter;
  onChange: (filterChange: FilterChange) => void;
};

export function EvoAirlinesFilter(props: EvoAirlinesFilterProps) {
  const { companies, sameAirline, onChange } = props;
  const { isTrain } = useUrlSearchParams();
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { sendCuriosityClickEvent } = useCuriosity();
  const sendStratrackFlightPageFilterEvent = useTransportPageFilterTracking();

  if (!companies || companies.length <= 1) return null;

  const sameAirlineLabel = t('vacation.oss.change_transport.filters.same_airline');
  const showSameAirlineToggle = isSameAirlineFilterEnabled(localization.configValuesApp) && !isTrain;

  return (
    <>
      {showSameAirlineToggle && (
        <>
          <SameAirlineToggleRow>
            <SameAirlineLabel htmlFor={SAME_AIRLINE_SWITCH_ID}>
              <Text variant="Body" color="textPrimary">
                {sameAirlineLabel}
              </Text>
            </SameAirlineLabel>
            <Switch
              id={SAME_AIRLINE_SWITCH_ID}
              label={sameAirlineLabel}
              isLabelVisible={false}
              checked={sameAirline?.on ?? true}
              onChange={(on: boolean) =>
                onChange({
                  newState: { sameAirline: { on } },
                  event: { type: 'FILTER', origin: 'AIRLINES_FILTER' },
                })
              }
            />
            <EvoFilterLoadingLayer origins={['AIRLINES_FILTER']} />
          </SameAirlineToggleRow>
          <EvoFilterDivider />
        </>
      )}
      <EvoFilterSection>
        {companies.map((company) => (
          <EvoCheckboxRow
            key={company.name}
            id={`evo-airline-${company.name}`}
            label={company.name}
            checked={company.selected}
            disabled={!company.enabled}
            onChange={() =>
              onChange(
                getCompaniesFilterChangeData(
                  isTrain,
                  companies,
                  company.name,
                  sendCuriosityClickEvent,
                  sendStratrackFlightPageFilterEvent
                )
              )
            }
          />
        ))}
        <EvoFilterLoadingLayer origins={['AIRLINES_FILTER']} />
      </EvoFilterSection>
    </>
  );
}
